/**
 * ThemeContext — drives the page-level `class` and CSS variables on
 * <html>. Supports two built-in themes (`light`, `dark`) plus any
 * additional themes registered at runtime by Marketplace extensions
 * (e.g. the AMOLED Dark Theme extension registers `amoled`).
 *
 * Extension-registered themes can supply CSS variable overrides via
 * the `config` arg. We render them as inline `style` on <html> so the
 * extension doesn't need to ship a stylesheet — a single CSS file per
 * theme name handles the structural rules.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '@/lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const BUILTIN_THEMES = ['light', 'dark'];

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem('li-theme') || 'dark');
  // extension themes registered by the Marketplace at runtime.
  // Shape: { name: { kind: 'dark'|'light', vars: {'--accent': '#...'}, label, source } }
  const [extensionThemes, setExtensionThemes] = useState({});

  // Apply theme on every change — toggle classes + write CSS variables.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove(...BUILTIN_THEMES, ...Object.keys(extensionThemes));
    // For an extension theme that's a "dark" variant, keep `dark` on
    // so all dark-mode Tailwind classes still apply.
    const ext = extensionThemes[theme];
    if (ext) {
      root.classList.add(ext.kind || 'dark');
      root.classList.add(theme);
      for (const [k, v] of Object.entries(ext.vars || {})) root.style.setProperty(k, v);
    } else {
      root.classList.add(theme);
      // Clear extension-set CSS variables when switching back to a builtin.
      Object.values(extensionThemes).forEach(e => {
        Object.keys(e.vars || {}).forEach(k => root.style.removeProperty(k));
      });
    }
    localStorage.setItem('li-theme', theme);
  }, [theme, extensionThemes]);

  // ── Extension theme registry ────────────────────────────────────
  const registerExtensionTheme = useCallback((name, def) => {
    setExtensionThemes(prev => ({ ...prev, [name]: def }));
  }, []);
  const unregisterExtensionTheme = useCallback((name) => {
    setExtensionThemes(prev => {
      const { [name]: _gone, ...rest } = prev;
      // If the user was on the deregistered theme, fall back to dark.
      setThemeState(t => t === name ? 'dark' : t);
      return rest;
    });
  }, []);

  // ── Marketplace ↔ theme bridge ──────────────────────────────────
  // On boot (and whenever the install set changes), discover any
  // installed theme extensions and register them. Best-effort — a
  // failure here must not break the app load.
  const syncFromMarketplace = useCallback(async () => {
    try {
      const { ok, data } = await apiFetch(`${API}/api/marketplace/installed`, { method: 'GET' });
      if (!ok || !Array.isArray(data)) return;
      // Drop any extension themes that aren't enabled anymore.
      const wantedNames = new Set();
      for (const inst of data) {
        if (!inst.enabled) continue;
        if (inst.extension?.kind !== 'theme') continue;
        const name = _themeNameForExtension(inst.extension_id);
        if (!name) continue;
        wantedNames.add(name);
        registerExtensionTheme(name, _buildThemeDef(inst));
      }
      // Prune themes whose extension was disabled / uninstalled.
      setExtensionThemes(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(n => { if (!wantedNames.has(n)) delete next[n]; });
        return next;
      });
    } catch {
      // Anonymous routes or pre-auth load — silently ignore.
    }
  }, [registerExtensionTheme]);

  // Re-sync on mount and on the custom "marketplace-changed" event the
  // MarketplacePage fires after an install / toggle.
  useEffect(() => {
    syncFromMarketplace();
    const handler = () => syncFromMarketplace();
    window.addEventListener('marketplace-changed', handler);
    return () => window.removeEventListener('marketplace-changed', handler);
  }, [syncFromMarketplace]);

  const setTheme = useCallback((name) => {
    // Validate against builtin + extension registry.
    if (BUILTIN_THEMES.includes(name) || extensionThemes[name]) setThemeState(name);
  }, [extensionThemes]);

  const toggleTheme = useCallback(() => {
    // Cycle through all available themes — keeps the existing
    // single-button UX even when extensions add new options.
    const available = [...BUILTIN_THEMES, ...Object.keys(extensionThemes)];
    setThemeState(t => {
      const idx = available.indexOf(t);
      return available[(idx + 1) % available.length];
    });
  }, [extensionThemes]);

  const availableThemes = [
    { name: 'light', label: 'Light', kind: 'light' },
    { name: 'dark',  label: 'Dark',  kind: 'dark' },
    ...Object.entries(extensionThemes).map(([name, def]) => ({ name, label: def.label || name, kind: def.kind || 'dark', source: 'extension' })),
  ];

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, toggleTheme, availableThemes,
      registerExtensionTheme, unregisterExtensionTheme, syncFromMarketplace,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

// ── Static mapping: marketplace extension_id → theme name ────────
// Keep this small and explicit; a new theme extension needs an entry
// here to be discoverable. (We intentionally avoid arbitrary JS-from-
// network execution — see the GAP-7 design notes.)
function _themeNameForExtension(extensionId) {
  if (extensionId === 'mkt_dark_amoled') return 'amoled';
  return null;
}

function _buildThemeDef(install) {
  if (install.extension_id === 'mkt_dark_amoled') {
    const accent = install.config?.accent_color || '#22d3ee';
    return {
      kind: 'dark',
      label: 'AMOLED',
      vars: {
        '--background': '0 0% 0%',         // pure black canvas
        '--card':       '0 0% 0%',
        '--popover':    '0 0% 4%',
        '--border':     '0 0% 8%',
        '--primary':    _hexToHslTokens(accent),
      },
      source: 'marketplace:mkt_dark_amoled',
    };
  }
  return { kind: 'dark', label: install.extension?.name || 'Custom' };
}

// Convert a #rrggbb hex into the "H S% L%" string our Tailwind theme uses.
function _hexToHslTokens(hex) {
  const m = (hex || '').match(/^#?([a-f0-9]{6})$/i);
  if (!m) return '174 72% 56%';   // cyan fallback
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
